import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NotificationItem {
  id: string;
  kind: 'message' | 'lead';
  label: string;
  sublabel: string;
  created_at: string;
  link: string;
}

const AdminHeader = () => {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    const since = new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString();
    const [msgs, leads] = await Promise.all([
      supabase
        .from('contact_messages')
        .select('id, name, email, created_at')
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(10),
      supabase
        .from('chatbot_leads')
        .select('id, email, phone, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(10),
    ]);

    const list: NotificationItem[] = [];
    (msgs.data ?? []).forEach((m) =>
      list.push({
        id: `msg-${m.id}`,
        kind: 'message',
        label: `Nová zpráva od ${m.name}`,
        sublabel: m.email,
        created_at: m.created_at,
        link: '/admin/messages',
      }),
    );
    (leads.data ?? []).forEach((l) =>
      list.push({
        id: `lead-${l.id}`,
        kind: 'lead',
        label: 'Nový lead z chatbota',
        sublabel: l.email || l.phone || 'bez kontaktu',
        created_at: l.created_at,
        link: '/admin/chatbot-leads',
      }),
    );
    list.sort((a, b) => b.created_at.localeCompare(a.created_at));
    setItems(list.slice(0, 10));
    setUnread((msgs.data ?? []).length + (leads.data ?? []).length);
  }, []);

  useEffect(() => {
    fetchUnread();
    const ch = supabase
      .channel('admin-notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'contact_messages' }, fetchUnread)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chatbot_leads' }, fetchUnread)
      .subscribe();
    const interval = setInterval(fetchUnread, 60000);
    return () => {
      supabase.removeChannel(ch);
      clearInterval(interval);
    };
  }, [fetchUnread]);

  return (
    <header className="sticky top-0 z-30 h-14 flex items-center justify-end px-6 border-b border-border bg-card/80 backdrop-blur">
      <DropdownMenu>
        <DropdownMenuTrigger className="relative p-2 rounded-md hover:bg-secondary/50 transition-colors">
          <Bell size={20} className="text-muted-foreground" />
          {unread > 0 && (
            <span className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel>Notifikace</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">Zatím nic nového</div>
          ) : (
            items.map((it) => (
              <DropdownMenuItem key={it.id} asChild className="cursor-pointer">
                <Link to={it.link} className="flex flex-col items-start gap-0.5 py-2">
                  <span className="text-sm font-medium text-foreground">{it.label}</span>
                  <span className="text-xs text-muted-foreground">{it.sublabel}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(it.created_at).toLocaleString('cs-CZ')}
                  </span>
                </Link>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

export default AdminHeader;
